import React, { useEffect, useMemo, useState } from "react";
import { apiFetch, jsonHeaders } from "../api/api";
import { Link } from "react-router-dom";
import logo from "../logo.png";
import passwordRevealed from "../assets/password_revealed.png";
import passwordNotRevealed from "../assets/password_not_revealed.png";

const iconStyle = { width: 24, height: 24, verticalAlign: "middle" };
const checkStyle = { color: "#22c55e", marginRight: 6, fontWeight: "bold" };
const crossStyle = { color: "#ef4444", marginRight: 6, fontWeight: "bold" };

function getCriteria(password) {
  return {
    length: password.length >= 12,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
}

// Signup page
const Signup = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    schoolCode: "",
    // MFA is forced to TOTP server-side; phone not required
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [emailDomainValid, setEmailDomainValid] = useState(null); // true/false/null
  const [schoolCodeValid, setSchoolCodeValid] = useState(null);
  const [schoolCodeCheckError, setSchoolCodeCheckError] = useState("");
  const [checkingCode, setCheckingCode] = useState(false);

  const criteria = getCriteria(form.password);

  const allowedDomains = useMemo(() => {
    const raw = (
      process.env.REACT_APP_ALLOWED_DOMAINS || "corvallis.k12.or.us"
    ).toLowerCase();
    return raw
      .split(",")
      .map((d) => d.trim())
      .filter(Boolean);
  }, []);
  const emailLower = (form.email || "").toLowerCase();
  const emailDomainOk = useMemo(() => {
    if (!emailLower.includes("@")) return false;
    return allowedDomains.some((d) => emailLower.endsWith("@" + d));
  }, [emailLower, allowedDomains]);

  // Live email domain feedback
  useEffect(() => {
    if (!form.email) {
      setEmailDomainValid(null);
      return;
    }
    setEmailDomainValid(emailDomainOk);
  }, [form.email, emailDomainOk]);

  // Debounced school code validation
  useEffect(() => {
    if (!form.schoolCode) {
      setSchoolCodeValid(null);
      setSchoolCodeCheckError("");
      return;
    }
    const t = setTimeout(async () => {
      try {
        setCheckingCode(true);
        const resp = await apiFetch(
          `/validate-school-code?code=${encodeURIComponent(form.schoolCode)}`
        );
        setSchoolCodeValid(Boolean(resp?.valid));
        setSchoolCodeCheckError("");
      } catch {
        // Don't mark invalid on server error; show a neutral message
        setSchoolCodeValid(null);
        setSchoolCodeCheckError("Unable to validate code right now.");
      } finally {
        setCheckingCode(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.schoolCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validatePassword = (password) => {
    return (
      password.length >= 12 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[^A-Za-z0-9]/.test(password)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    // Client-side explicit domain check to give immediate guidance
    if (!emailDomainOk) {
      setError(
        `Please use an approved email domain (${allowedDomains.join(", ")}).`
      );
      setSubmitting(false);
      return;
    }
    if (!validatePassword(form.password)) {
      setError(
        "Password must be at least 12 characters and include uppercase, lowercase, number, and special character."
      );
      setSubmitting(false);
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      setSubmitting(false);
      return;
    }
    try {
      await apiFetch(`/signup`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          password: form.password,
          schoolCode: form.schoolCode,
          // Server will default mfaMethod to 'totp'
        }),
      });
      setSuccess("Account created! Please check your email to verify.");
    } catch (err) {
      // Provide actionable messages
      const msg = (err?.message || "Signup failed").toString();
      if (
        err?.status === 403 &&
        msg.toLowerCase().includes("email domain not approved")
      ) {
        setError(
          "Your email domain is not approved. Please use your district email (@corvallis.k12.or.us)."
        );
      } else if (
        err?.status === 400 &&
        msg.toLowerCase().includes("invalid or unrecognized school code")
      ) {
        setError(
          "Invalid school code. Please double-check the code provided by your administrator."
        );
      } else if (
        err?.status === 400 &&
        msg.toLowerCase().includes("missing fields")
      ) {
        setError(
          "Some required fields are missing. Please complete all fields and try again."
        );
      } else if (err?.status === 500) {
        setError(
          "Server error. If you used a non-district email, switch to @corvallis.k12.or.us. If the school code might be wrong, double-check it and try again."
        );
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          marginTop: 32,
          marginBottom: 32,
        }}
      >
        <img
          src={logo}
          alt="Counselor Notes Logo"
          style={{ width: 400, maxWidth: "90vw" }}
        />
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
          width: 320,
        }}
      >
        <h1 style={{ textAlign: "center" }}>Sign Up</h1>
        <form
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 20,
            marginTop: 32,
          }}
          onSubmit={handleSubmit}
        >
          <input
            name="firstName"
            type="text"
            placeholder="First Name"
            value={form.firstName}
            onChange={handleChange}
            style={{
              padding: 12,
              fontSize: 16,
              borderRadius: 4,
              border: "1px solid #ccc",
            }}
            required
          />
          <input
            name="lastName"
            type="text"
            placeholder="Last Name"
            value={form.lastName}
            onChange={handleChange}
            style={{
              padding: 12,
              fontSize: 16,
              borderRadius: 4,
              border: "1px solid #ccc",
            }}
            required
          />
          <input
            name="email"
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
            style={{
              padding: 12,
              fontSize: 16,
              borderRadius: 4,
              border:
                emailDomainValid === false
                  ? "1px solid #ef4444"
                  : "1px solid #ccc",
            }}
            required
          />
          {emailDomainValid === false && (
            <div style={{ color: "#ef4444", fontSize: 12, marginTop: -10 }}>
              Invalid email domain. Allowed: {allowedDomains.join(", ")}
            </div>
          )}
          <div style={{ position: "relative" }}>
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              style={{
                padding: 12,
                fontSize: 16,
                borderRadius: 4,
                border: "1px solid #ccc",
                width: "92%",
              }}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={showPassword ? passwordRevealed : passwordNotRevealed}
                alt={showPassword ? "Hide password" : "Show password"}
                style={iconStyle}
              />
            </button>
          </div>
          <div
            style={{
              fontSize: 13,
              color: "#555",
              marginTop: -10,
              marginBottom: -10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              {criteria.length ? (
                <span style={checkStyle}>✓</span>
              ) : (
                <span style={crossStyle}>✗</span>
              )}
              At least 12 characters
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              {criteria.upper ? (
                <span style={checkStyle}>✓</span>
              ) : (
                <span style={crossStyle}>✗</span>
              )}
              Uppercase letter
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              {criteria.lower ? (
                <span style={checkStyle}>✓</span>
              ) : (
                <span style={crossStyle}>✗</span>
              )}
              Lowercase letter
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              {criteria.number ? (
                <span style={checkStyle}>✓</span>
              ) : (
                <span style={crossStyle}>✗</span>
              )}
              Number
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              {criteria.special ? (
                <span style={checkStyle}>✓</span>
              ) : (
                <span style={crossStyle}>✗</span>
              )}
              Special character
            </div>
          </div>
          <div style={{ position: "relative" }}>
            <input
              name="confirmPassword"
              type={showConfirm ? "text" : "password"}
              placeholder="Confirm Password"
              value={form.confirmPassword}
              onChange={handleChange}
              style={{
                padding: 12,
                fontSize: 16,
                borderRadius: 4,
                border: "1px solid #ccc",
                width: "92%",
              }}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={showConfirm ? passwordRevealed : passwordNotRevealed}
                alt={showConfirm ? "Hide password" : "Show password"}
                style={iconStyle}
              />
            </button>
          </div>
          {/* Password match message below confirm password */}
          {form.confirmPassword.length > 0 && (
            <div
              style={{
                marginTop: 4,
                fontSize: 14,
                fontWeight: 500,
                color:
                  form.password === form.confirmPassword
                    ? "#22c55e"
                    : "#ef4444",
                minHeight: 20,
              }}
              aria-live="polite"
            >
              {form.password === form.confirmPassword
                ? "Passwords match!"
                : "Passwords do not match"}
            </div>
          )}
          <input
            name="schoolCode"
            type="text"
            placeholder="School Code"
            value={form.schoolCode}
            onChange={handleChange}
            style={{
              padding: 12,
              fontSize: 16,
              borderRadius: 4,
              border:
                schoolCodeValid === false
                  ? "1px solid #ef4444"
                  : "1px solid #ccc",
            }}
            required
          />
          {form.schoolCode && (
            <div style={{ fontSize: 12, marginTop: -8 }}>
              {checkingCode ? (
                <span style={{ color: "#6b7280" }}>Checking school code…</span>
              ) : schoolCodeValid === true ? (
                <span style={{ color: "#22c55e" }}>
                  School code looks good.
                </span>
              ) : schoolCodeValid === false ? (
                <span style={{ color: "#ef4444" }}>
                  Invalid school code. Please verify with your administrator.
                </span>
              ) : schoolCodeCheckError ? (
                <span style={{ color: "#6b7280" }}>{schoolCodeCheckError}</span>
              ) : null}
            </div>
          )}
          {/* MFA is enforced as TOTP by default; phone collection removed */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: "100%",
              padding: "16px 0",
              fontSize: 20,
              background: "#2563eb",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              transition: "background 0.2s",
            }}
          >
            {submitting ? "Creating..." : "Create Account"}
          </button>
        </form>
        {error && <div style={{ color: "red", marginTop: 16 }}>{error}</div>}
        {success && (
          <div style={{ color: "green", marginTop: 16 }}>{success}</div>
        )}
        <div style={{ marginTop: 24, textAlign: "center" }}>
          Already have an account? <Link to="/login">Login</Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
